// Do not import { store } from "@root/store". Analytics is loaded from main.tsx
// (error listeners) before/alongside store init; a top-level store import
// participated in the persistedData ↔ store cycle (prod TDZ crash).
import { getStore } from "@root/store/storeRef";

const CLIENT_ID_STORAGE_KEY = "ga_client_id";
// Fallback if VITE_TRACK_URL is unset. Production should set VITE_TRACK_URL
// to the Cloudflare Worker (see workers/track).
// const DEFAULT_TRACK_URL = "/.netlify/functions/track";
const DEFAULT_TRACK_URL = "http://127.0.0.1:8787";

let memoryClientId = null;

function generateId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}.${Math.floor(Math.random() * 1e9)}`;
}

function readStorage(storage) {
  try {
    return storage?.getItem?.(CLIENT_ID_STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

function writeStorage(storage, value) {
  try {
    storage?.setItem?.(CLIENT_ID_STORAGE_KEY, value);
  } catch {
    // WebView may block storage; ignore
  }
}

/**
 * Cookie-free client id: memory → sessionStorage → localStorage → generate.
 * Never depends on cookies (disabled in this WebView).
 */
export function getAnalyticsClientId() {
  if (memoryClientId) return memoryClientId;

  if (typeof window !== "undefined") {
    const fromSession = readStorage(window.sessionStorage);
    if (fromSession) {
      memoryClientId = fromSession;
      return memoryClientId;
    }
    const fromLocal = readStorage(window.localStorage);
    if (fromLocal) {
      memoryClientId = fromLocal;
      writeStorage(window.sessionStorage, fromLocal);
      return memoryClientId;
    }
  }

  memoryClientId = generateId();
  if (typeof window !== "undefined") {
    writeStorage(window.sessionStorage, memoryClientId);
    writeStorage(window.localStorage, memoryClientId);
  }
  return memoryClientId;
}

function getStoreState() {
  try {
    // Null only if an event fires before setStore (e.g. a crash during createStore).
    const store = getStore();
    return store?.getState?.() || null;
  } catch {
    return null;
  }
}

function getUserIdFromStore() {
  try {
    const id = getStoreState()?.overviewReducer?.userDetails?.responseData?.user?.id;
    return id != null && id !== "" ? String(id) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Native Android sets window.androidData (JSON string) via updateBuildInfo.
 * Shape: { deviceInfo: { sdk, ... } }. Missing on iOS / before the bridge fires.
 */
function getAndroidDeviceInfo() {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = window.androidData;
    // Local try/catch only — do not use safeJsonParse here (it calls
    // trackAnalyticsEvent → getPageContextParams → this function).
    if (raw == null || raw === "") return undefined;
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (parsed == null) return undefined;
    const info = parsed.deviceInfo != null ? parsed.deviceInfo : parsed;
    return typeof info === "string" ? info : JSON.stringify(info);
  } catch {
    return undefined;
  }
}

function getDeviceInfoParams() {
  const params = {};
  const deviceInfo = getAndroidDeviceInfo();
  if (deviceInfo) {
    params.device_info = deviceInfo;
  }
  return params;
}

/** Context previously sent on page_view; attached to every other event instead. */
function getPageContextParams() {
  try {
    const state = getStoreState() || {};
    const org = state?.persistedDataReducer?.orgDetails?.organization;
    const user = state?.overviewReducer?.userDetails?.responseData?.user;
    const courseMetaId =
      state?.overviewReducer?.overViewDetails?.course?.metaData?.id;
    const deviceType = state?.persistedDataReducer?.deviceType;

    let pagePath = "";
    let pageTitle = "";
    let courseIdFromPath;
    if (typeof window !== "undefined") {
      pagePath = `${window.location.pathname}${window.location.search}`;
      pageTitle = typeof document !== "undefined" ? document.title : "";
      const first = window.location.pathname.split("/").filter(Boolean)[0];
      if (first && !Number.isNaN(Number(first))) {
        courseIdFromPath = Number(first);
      }
    }

    const courseId = courseMetaId || courseIdFromPath;

    return {
      page_path: pagePath,
      page_title: pageTitle,
      course_id: courseId ? Number(courseId) : undefined,
      org_id: org?.id ? Number(org.id) : undefined,
      org_code: org?.code ? `${org.code}` : undefined,
      cp_user_id: user?.id ? Number(user.id) : undefined,
      cp_sdk_source: deviceType != "IOS" ? "android" : "ios",
      screen_name: "course",
      ...getDeviceInfoParams(),
    };
  } catch {
    return {};
  }
}

function getTrackUrl() {
  const fromEnv =
    typeof import.meta !== "undefined" && import.meta.env?.VITE_TRACK_URL;
  return fromEnv || DEFAULT_TRACK_URL;
}

/** Drop origin and anonymous frames so GA4's 100-char param keeps the path. */
export function stripUrlOrigins(text) {
  if (!text || typeof text !== "string") return text;
  return text
    .replace(/https?:\/\/[^/\s]+/gi, "")
    .replace(/\s*\(<anonymous>\)/g, "")
    .replace(/\n{2,}/g, "\n")
    .trim();
}

function paramsForGa(properties = {}) {
  const next = { ...properties };
  if (typeof next.message === "string") {
    next.message = stripUrlOrigins(next.message);
  }
  if (typeof next.stack_snippet === "string") {
    next.stack_snippet = stripUrlOrigins(next.stack_snippet);
  }
  if (typeof next.component_stack === "string") {
    next.component_stack = stripUrlOrigins(next.component_stack);
  }
  return next;
}

/**
 * Client-initiated analytics for cookie-disabled WebViews.
 * POSTs to the track proxy (Cloudflare Worker via VITE_TRACK_URL) → GA4.
 * Merges page context (path, course, org, user, sdk) into every event.
 * Separate from the native onGTMEvent bridge in trackGtmEvents.js.
 */
export const trackAnalyticsEvent = (event, properties = {}) => {
  try {
    if (typeof window === "undefined") return;

    const userId = getUserIdFromStore();
    const clientId = userId || getAnalyticsClientId();
    const body = {
      event,
      client_id: clientId,
      ...(userId ? { user_id: userId } : {}),
      params: paramsForGa({ ...getPageContextParams(), ...properties }),
    };

    console.log("[analytics track]", event, body);

    fetch(getTrackUrl(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {
      // Fire-and-forget; never break the app
    });
  } catch {
    // Never let analytics break the app
  }
};

/** @deprecated Use trackAnalyticsEvent */
export const pushGtmDataLayerEvent = trackAnalyticsEvent;
